import { Spin, Tooltip, message } from 'antd';
import React, { useState, useCallback, useEffect, useRef, ReactElement, useMemo } from 'react';
import Icon, { ExclamationCircleOutlined } from '@ant-design/icons';
import {
  useDeployWorkbench,
  useExportWorkbench,
  useDeployCandidateList,
  useGetWorkbenchLatestDeployInfo,
  useGetWorkbenchDeployAvailableInterval,
} from '@api/emp/queryHooks';
import deployLined from '@icons/emp/deploy.svg';
import ArrowRightOutlinedIcon from '@icons/sankey/arrow-right-outlined.svg';
import { WorkbenchDeploymentDrawer } from 'emp-3.0/components/DeploymentDrawer/workbench';
import { useCreatePermissions } from '@src/hooks/usePermissions';
import { trackClickDeployWorkbench } from '@src/sensor';
import PermissionButton from '@src/emp-3.0/components/PermissionButton';
import { useInitialInfoStore } from '@src/emp-3.0/store/useInitialInfo';
import { WorkbenchAction } from '@src/emp-3.0/components/DeploymentDrawer/typed';
import styled from 'styled-components';
import { WorkbenchDeployStatus, WorkbenchDeployInfo } from '@src/typings/workbench';
import moment from 'moment';
import { set } from 'lodash';

export interface DeployButtonRef {
  click: () => void;
}

const DEPLOY_FAIL_MSG = `Failed to deploy, please try again later`;
const DEPLOY_SUCCESS_MSG = `Deploy success`;

interface DeployButtonProps {
  text?: string;
  style?: React.CSSProperties;
  metricType?: string;
  enableSecondPanel?: boolean;
}

const warningColor = '#fa8c16';

const CustomSpin = styled(Spin)`
  i {
    background-color: #fa8c16 !important; /* your color */
  }
`;

const statusIcon: Record<WorkbenchDeployStatus, ReactElement> = {
  available: <ExclamationCircleOutlined style={{ color: '#52C41A', marginRight: 8 }} />,
  failed: <ExclamationCircleOutlined style={{ color: '#F5222D', marginRight: 8 }} />,
  running: <CustomSpin size="small" style={{ marginRight: 8 }} />,
  pending: <CustomSpin size="small" style={{ marginRight: 8 }} />,
};

function getTipIcon(requesting: boolean, deployInfo?: WorkbenchDeployInfo) {
  const status: WorkbenchDeployStatus =
    requesting || deployInfo === undefined ? 'running' : deployInfo.status;
  let TipContent: ReactElement | null = null;

  try {
    if (deployInfo && deployInfo.created_at) {
      const { created_at, username, status, interval, updated_at } = deployInfo;
      const message = {
        Status: status === 'available' ? 'success' : status,
        'Deployed At': moment(created_at).format(),
        'Deployed By': username,
        'Time Range': interval,
        'Execution Time': moment.duration(moment(updated_at).diff(moment(created_at))).humanize(),
      };
      TipContent = (
        <div>
          {Object.entries(message).map(([key, value]) => {
            return <div key={key}>{`• ${key}: ${value}`}</div>;
          })}
        </div>
      );
    }
  } catch (err) {
    /** Ignore */
  }
  const results: ReactElement[] = [];
  if (statusIcon[status]) {
    if (TipContent) {
      results.push(
        <Tooltip
          title={TipContent}
          placement="bottomLeft"
          styles={{
            root: {
              minWidth: 500,
            },
            body: {
              color: '#fff',
              fontSize: 14,
              borderRadius: 8,
              background: '#222',
              lineHeight: 1.6,
            },
          }}
        >
          {statusIcon[status]}
        </Tooltip>,
      );
    } else {
      results.push(statusIcon[status]);
    }
  }
  results.push(<Icon component={deployLined} style={{ color: warningColor, marginRight: 4 }} />);
  return results;
}

export const WorkbenchDeployButtons = ({
  metricType,
  enableSecondPanel = false,
}: DeployButtonProps) => {
  const { workbenchMode } = useInitialInfoStore();
  const [activeDeployTaskId, setActiveDeployTaskId] = useState<string>('');
  const [isExporting, setIsExporting] = useState(false);
  const [isDeploymentDrawerOpen, setIsDeploymentDrawerOpen] = useState(false);
  const [deploySuccess, setDeploySuccess] = useState(false);
  const [action, setAction] = useState<WorkbenchAction>(WorkbenchAction.deployWorkBench);

  const { mutate: deployWorkbench, isLoading: isDeployingMutation } = useDeployWorkbench();
  const { mutate: exportWorkbench } = useExportWorkbench();
  const { refetch: refetchCandidateList } = useDeployCandidateList();
  const { refetch: refetchWorkbenchDeployAvailableInterval } =
    useGetWorkbenchDeployAvailableInterval();

  const hasCreate = useCreatePermissions();

  const {
    data: deployInfo,
    isFetching: isFetchingDeployInfo,
    refetch: refetchWorkbenchDeployInfo,
  } = useGetWorkbenchLatestDeployInfo();

  const isDeploying = useMemo(() => {
    // Show loading if deploy mutation is in progress
    if (isDeployingMutation) return true;

    if (!deployInfo) return true;

    const { status, deploy_task_id } = deployInfo;

    // If we're tracking a specific deployment
    if (activeDeployTaskId) {
      // Check if it's still in progress or if we haven't received the update yet
      return deploy_task_id !== activeDeployTaskId || status === 'running' || status === 'pending';
    }

    // Auto-detect ongoing deployments (e.g., after page refresh)
    return status === 'running' || status === 'pending';
  }, [isDeployingMutation, activeDeployTaskId, deployInfo]);

  // Handle deployment status changes
  useEffect(() => {
    if (!deployInfo || !activeDeployTaskId) return;

    const { deploy_task_id, status, message: msgFromServer } = deployInfo;

    // Ignore responses for different deployments
    if (deploy_task_id !== activeDeployTaskId) return;

    // Still in progress
    if (status === 'running' || status === 'pending') return;

    // Deployment finished - clear tracking and show result
    setActiveDeployTaskId('');

    if (status === 'failed') {
      message.error(msgFromServer || DEPLOY_FAIL_MSG);
    } else if (status === 'available') {
      message.success(msgFromServer || DEPLOY_SUCCESS_MSG);
    }
  }, [deployInfo, activeDeployTaskId]);

  const onOpenDeploymentDrawer = useCallback(
    (newAction: WorkbenchAction) => {
      setAction(newAction);
      setIsDeploymentDrawerOpen(true);
      refetchCandidateList();
      if (newAction === WorkbenchAction.deployWorkBench) {
        refetchWorkbenchDeployInfo();
        refetchWorkbenchDeployAvailableInterval();
      }
    },
    [refetchCandidateList, refetchWorkbenchDeployAvailableInterval, refetchWorkbenchDeployInfo],
  );

  const handleClickDeploy = (value) => {
    if (!hasCreate) return;

    trackClickDeployWorkbench({});

    deployWorkbench(value, {
      onSuccess: (data) => {
        const {
          deploy_task_id,
          status,
          message: msgFromServer,
        } = (data?.data ?? {}) as WorkbenchDeployInfo;

        if (status === 'running' || status === 'pending') {
          // Start tracking this deployment
          setActiveDeployTaskId(deploy_task_id);
          message.success(`Deployment ${status}`);
          refetchCandidateList();
          // Trigger initial fetch and start polling
          refetchWorkbenchDeployInfo();
        } else if (status === 'failed') {
          message.error(msgFromServer || DEPLOY_FAIL_MSG);
        } else if (status === 'available') {
          message.success(msgFromServer || DEPLOY_SUCCESS_MSG);
        }
      },
      onError: (err) => {
        message.error((err as Error)?.message || DEPLOY_FAIL_MSG);
      },
    });
  };

  const handleClickExport = (value) => {
    if (!hasCreate) {
      return;
    }
    setIsExporting(true);
    exportWorkbench(value, {
      onSuccess: () => {
        message.success('Export to Production Activation Success');
        setIsDeploymentDrawerOpen(false);
      },
      onError: (err) => {
        const errorMessage =
          (err as any)?.response?.data?.msg ||
          (err as Error)?.message ||
          'Failed to export, please try again later!';
        message.error(errorMessage);
      },
      onSettled: () => {
        setIsExporting(false);
      },
    });
  };

  if (!workbenchMode) {
    return null;
  }

  return (
    <div style={{ display: 'inline-block' }}>
      <PermissionButton
        tooltip=""
        onClick={() => onOpenDeploymentDrawer(WorkbenchAction.exportToProduction)}
        styleType="styled"
        style={{
          color: warningColor,
          border: `1px solid ${warningColor}`,
          marginRight: '8px',
        }}
      >
        <>
          <ArrowRightOutlinedIcon style={{ fill: 'currentColor' }} />
          Export to Production Activation
        </>
      </PermissionButton>
      <PermissionButton
        tooltip=""
        onClick={() => onOpenDeploymentDrawer(WorkbenchAction.deployWorkBench)}
        styleType="styled"
        style={{
          color: warningColor,
          border: `1px solid ${warningColor}`,
        }}
      >
        <>
          {[...getTipIcon(isFetchingDeployInfo || isDeploying, deployInfo), 'Deploy (Workbench)']}
        </>
      </PermissionButton>

      <WorkbenchDeploymentDrawer
        action={action}
        enableSecondPanel={enableSecondPanel}
        deploySuccess={deploySuccess}
        metricType={metricType}
        isDeploying={isDeploying}
        isExporting={isExporting}
        drawerProps={{
          open: isDeploymentDrawerOpen,
          afterOpenChange: (open) => {
            if (!open) {
              setDeploySuccess(false);
            }
          },
          onClose: () => {
            setIsDeploymentDrawerOpen(false);
          },
        }}
        onDeploy={handleClickDeploy}
        onExport={handleClickExport}
      />
    </div>
  );
};
